import { useState } from 'react'

import { useUndoRedoContext } from '@/context/UndoRedoContext'

export function UndoManagerDevtools() {
  const [isOpen, setIsOpen] = useState(false)
  const { undoStack, redoStack } = useUndoRedoContext()

  return (
    <>
      {/* Toggle Button - Always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='fixed bottom-4 right-4 z-[9999] bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg shadow-lg font-semibold text-m-m flex items-center gap-2'
        title={isOpen ? 'Minimize Undo/Redo Debugger' : 'Open Undo/Redo Debugger'}
      >
        <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
          />
        </svg>
        Undo/Redo
      </button>

      {/* Popup Panel */}
      {isOpen && (
        <div className='fixed bottom-16 right-4 z-[9999] w-[600px] h-[450px] bg-gray-900 text-white rounded-lg border-2 border-purple-600 shadow-2xl flex flex-col'>
          {/* Header */}
          <div className='flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700 rounded-t-lg'>
            <div className='flex items-center gap-3'>
              <svg className='w-3 h-3 text-purple-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              <h3 className='font-semibold text-m-m'>Undo/Redo Debugger</h3>
              <div className='flex gap-2 text-m-s'>
                <span className='bg-blue-600 px-2 py-0.5 rounded'>Undo: {undoStack.length}</span>
                <span className='bg-green-600 px-2 py-0.5 rounded'>Redo: {redoStack.length}</span>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className='cursor-pointer text-gray-400 hover:text-white transition-colors'
              title='Close Debugger'
            >
              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className='flex flex-1 divide-x divide-gray-700 overflow-hidden'>
            {/* Undo Stack */}
            <div className='flex-1 flex flex-col overflow-hidden'>
              <div className='bg-gray-800 px-4 py-2 text-xs font-semibold text-blue-400 border-b border-gray-700'>
                Undo Stack ({undoStack.length})
              </div>
              <div className='flex-1 overflow-y-auto p-2 space-y-1'>
                {undoStack.length === 0 ? (
                  <div className='text-center text-gray-500 text-m-s py-8'>No actions to undo</div>
                ) : (
                  [...undoStack].reverse().map((action, index) => (
                    <div
                      key={action.id}
                      className='bg-gray-800 hover:bg-gray-750 rounded p-2.5 border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer'
                      title={`Click to view details`}
                    >
                      <div className='flex items-start justify-between gap-2'>
                        <div className='flex-1 min-w-0'>
                          <div className='text-m-s font-medium truncate'>{action.description}</div>
                          <div className='flex items-center gap-2 mt-1.5 text-m-s text-gray-400'>
                            <span className='text-m-sm px-1.5 bg-gray-700 rounded'>{action.target}</span>
                            <span className='text-m-sm'>{new Date(action.timestamp).toLocaleTimeString()}</span>
                          </div>
                        </div>
                        <div className='text-m-s text-gray-500 whitespace-nowrap'>#{undoStack.length - index}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Redo Stack */}
            <div className='flex-1 flex flex-col overflow-hidden'>
              <div className='bg-gray-800 px-4 py-2 text-xs font-semibold text-green-400 border-b border-gray-700'>
                Redo Stack ({redoStack.length})
              </div>
              <div className='flex-1 overflow-y-auto p-2 space-y-1'>
                {redoStack.length === 0 ? (
                  <div className='text-center text-gray-500 text-m-s py-8'>No actions to redo</div>
                ) : (
                  [...redoStack].reverse().map((action, index) => (
                    <div
                      key={action.id}
                      className='bg-gray-800 hover:bg-gray-750 rounded p-2.5 border border-gray-700 hover:border-green-500 transition-colors cursor-pointer'
                      title={`Click to view details`}
                    >
                      <div className='flex items-start justify-between gap-2'>
                        <div className='flex-1 min-w-0'>
                          <div className='text-m-s font-medium truncate'>{action.description}</div>
                          <div className='flex items-center gap-2 mt-1.5 text-m-s text-gray-400'>
                            <span className='text-m-sm px-1.5 bg-gray-700 rounded'>{action.target}</span>
                            <span className='text-m-sm'>{new Date(action.timestamp).toLocaleTimeString()}</span>
                          </div>
                        </div>
                        <div className='text-m-s text-gray-500 whitespace-nowrap'>#{redoStack.length - index}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
